import React from "react";
import { Grid, Chip } from "@material-ui/core";
import { CustomTextField } from "./SellAiStyledComponents";
import { getCenterStoneCarats, getSideStoneCarats } from "./WizardStep3Weight";
import { isOtherJewelleryMetal } from "./WizardStep2AIRead";

interface WizardStep4PreviewProps {
  aiData: any;
  actualWeight: string;
}

const ReadOnlyChipField = ({ value, suffix = "" }: { value: any, suffix?: string }) => {
  const arr = Array.isArray(value) ? value : ((value !== null && value !== undefined && value !== "") ? String(value).split(',').map(s => s.trim()).filter(Boolean) : []);

  return (
    <div style={{
      border: '1px solid rgba(0, 0, 0, 0.23)',
      borderRadius: '4px',
      background: '#fff',
      padding: '4px 8px',
      minHeight: '38px',
      display: 'flex',
      alignItems: 'center'
    }}>
      {arr.length === 0 ? null : (
        <div style={{
          display: 'flex',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          width: '100%',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          {arr.map((item, index) => (
            <Chip
              key={index}
              size="small"
              label={item + suffix}
              style={{
                flexShrink: 0,
                marginRight: '4px',
                height: '24px',
                backgroundColor: 'rgba(217, 43, 60, 0.08)',
                color: '#333333',
                border: '1px solid rgba(217, 43, 60, 0.2)',
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const WizardStep4Preview: React.FC<WizardStep4PreviewProps> = ({ aiData, actualWeight }) => {
  const itemTypeStr = (aiData?.itemType ?? aiData?.category ?? "").trim().toLowerCase();
  const isDiamond = itemTypeStr.includes("diamond");

  const grossNum = parseFloat(actualWeight) || 0;
  const centerStoneCarats = getCenterStoneCarats(aiData);
  const sideStoneCarats = getSideStoneCarats(aiData);
  const netNum = grossNum > 0 ? Math.max(0, grossNum - (centerStoneCarats * 0.2) - (sideStoneCarats * 0.2)) : null;

  return (
    <div style={{ textAlign: 'left', marginBottom: '1rem', width: '100%' }}>
      <h3 style={{ fontSize: '1.2rem', fontFamily: 'Playfair Display, serif', fontWeight: 600, margin: '0 0 0.5rem 0', color: '#333' }}>
        Review your details
      </h3>
      <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 2rem 0' }}>
        Here's everything we have so far. Go back to change anything, or continue.
      </p>

      <Grid container spacing={4}>
        {/* General Details Section */}
        <Grid item xs={12}>
          <div style={{ border: '1px solid #EAEAEA', borderRadius: '12px', padding: '1.5rem', background: '#FAFAFA' }}>
            <Grid container spacing={4}>
              {/* Row 1 */}
              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>ITEM TYPE</span>
                </div>
                <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={aiData?.itemType ?? aiData?.category ?? ""} style={{ background: '#f5f5f5' }} />
              </Grid>

              {!isDiamond && (
                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>METAL</span>
                  </div>
                  <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={aiData?.metal || ""} style={{ background: '#f5f5f5' }} />
                </Grid>
              )}

              {/* Row 2 */}
              {!isDiamond && (
                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>SUB METAL</span>
                  </div>
                  <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={aiData?.subMetal || ""} style={{ background: '#f5f5f5' }} />
                </Grid>
              )}

              {!isDiamond && !isOtherJewelleryMetal(aiData?.metal) && (
                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>METAL FINENESS / PURITY</span>
                  </div>
                  <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={aiData?.metalPurity || ""} style={{ background: '#f5f5f5' }} />
                </Grid>
              )}

              {/* Row 3 */}
              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>ORIGIN</span>
                </div>
                <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={aiData?.originConfirmed ? (aiData?.originType || "Natural") : "Not confirmed"} style={{ background: '#f5f5f5' }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CERTIFICATE</span>
                </div>
                <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={aiData?.certificateAndReportNumber || ""} style={{ background: '#f5f5f5' }} />
              </Grid>
            </Grid>
          </div>
        </Grid>

        {/* Centre Stone Section */}
        {aiData?.centreStoneEnabled !== false && (
          <Grid item xs={12}>
            <div style={{ border: '1px solid #EAEAEA', borderRadius: '12px', padding: '1.5rem', background: '#FAFAFA' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>
                  CENTRE STONE
                </div>
              </div>

              <Grid container spacing={4}>
                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>STONE TYPE</span>
                  </div>
                  <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={aiData?.centreStone || "Diamond"} style={{ background: '#f5f5f5' }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>SHAPE</span>
                  </div>
                  <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={aiData?.shape || ""} style={{ background: '#f5f5f5' }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CUT</span>
                  </div>
                  <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={aiData?.cut || ""} style={{ background: '#f5f5f5' }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CARAT</span>
                  </div>
                  <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={(aiData?.carat !== null && aiData?.carat !== undefined && aiData?.carat !== "") ? `${aiData.carat} ct` : ""} style={{ background: '#f5f5f5' }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>COLOUR</span>
                  </div>
                  <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={aiData?.color || ""} style={{ background: '#f5f5f5' }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CLARITY</span>
                  </div>
                  <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={aiData?.clarity || ""} style={{ background: '#f5f5f5' }} />
                </Grid>
              </Grid>
            </div>
          </Grid>
        )}

        {/* Side Stone Section */}
        {(
          (aiData?.sideStone?.length) ||
          (aiData?.sideStoneShape?.length) ||
          (aiData?.sideStoneCut?.length) ||
          (aiData?.sideStoneColor?.length) ||
          (aiData?.sideStoneClarity?.length) ||
          (aiData?.sideStoneCarat !== undefined && aiData?.sideStoneCarat !== null && aiData?.sideStoneCarat !== "") ||
          (aiData?.noOfSideStones !== undefined && aiData?.noOfSideStones !== null && aiData?.noOfSideStones !== "")
        ) ? (
          <Grid item xs={12}>
            <div style={{ border: '1px solid #EAEAEA', borderRadius: '12px', padding: '1.5rem', background: '#FAFAFA' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>
                  SIDE STONE
                </div>
              </div>

              <Grid container spacing={4}>
                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>STONE TYPE</span>
                  </div>
                  <ReadOnlyChipField value={aiData?.sideStone} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>SHAPE</span>
                  </div>
                  <ReadOnlyChipField value={aiData?.sideStoneShape} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CUT</span>
                  </div>
                  <ReadOnlyChipField value={aiData?.sideStoneCut} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CARAT</span>
                  </div>
                  <CustomTextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    value={(aiData?.sideStoneCarat !== null && aiData?.sideStoneCarat !== undefined && aiData?.sideStoneCarat !== "") ? `${Array.isArray(aiData.sideStoneCarat) ? aiData.sideStoneCarat.join(', ') : aiData.sideStoneCarat}${String(aiData.sideStoneCarat).includes("ct") ? "" : " ct"}` : ""}
                    style={{ background: '#f5f5f5' }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>COLOUR</span>
                  </div>
                  <ReadOnlyChipField value={aiData?.sideStoneColor} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CLARITY</span>
                  </div>
                  <ReadOnlyChipField value={aiData?.sideStoneClarity} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>NO. OF STONES</span>
                  </div>
                  <CustomTextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    value={Array.isArray(aiData?.noOfSideStones) ? aiData.noOfSideStones.join(', ') : (aiData?.noOfSideStones || "")}
                    style={{ background: '#f5f5f5' }}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        ) : null}

        {/* Weight Section */}
        <Grid item xs={12}>
          <div style={{ border: '1px solid #EAEAEA', borderRadius: '12px', padding: '1.5rem', background: '#FAFAFA' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>
                WEIGHT
              </div>
            </div>

            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>GROSS WEIGHT</span>
                </div>
                <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={actualWeight ? `${actualWeight} g` : ""} style={{ background: '#f5f5f5' }} />
              </Grid>

              {netNum != null && (
                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>NET WEIGHT</span>
                  </div>
                  <CustomTextField variant="outlined" fullWidth size="small" InputProps={{ readOnly: true }} value={`${netNum.toFixed(2)} g`} style={{ background: '#f5f5f5' }} />
                </Grid>
              )}

            </Grid>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default WizardStep4Preview;
